import { AttachmentFileType, AttachmentFileAttributes } from 'owa-attachment-file-types';
import { ApiError, ApiErrorCode } from 'owa-addins-core';
import { AttachmentState, lazyDeleteAttachmentsViaQueueManager } from 'owa-attachment-well-data';
import { ClientItemId } from 'owa-client-ids';
import ComposeViewState from 'owa-mail-compose-store/lib/store/schema/ComposeViewState';
import addFile from 'owa-mail-compose-store/lib/utils/addFile';
import addItem from 'owa-mail-compose-store/lib/utils/addItem';
import createAttachmentFromBase64String from 'owa-mail-compose-store/lib/utils/createAttachmentFromBase64String';
import { getCurrentTableMailboxInfo } from 'owa-mail-mailboxinfo';
import { AttachmentDetails } from 'owa-addins-core';
import {
    infoBarAddHandler,
    infoBarRemoveHandler,
} from 'owa-mail-compose-store/lib/utils/infoBarHandlers';
import { lazyGetAllAttachments } from 'owa-addins-common-utils';

function getAttachmentError(messageId: string): ApiErrorCode {
    switch (messageId) {
        case 'ErrorTooLargeForLocalAttachmentSingle':
        case 'ErrorTooLargeForLocalAttachmentMultiple':
            return ApiErrorCode.AttachmentSizeExceeded;
        default:
            return ApiErrorCode.AttachmentUploadGeneralFailure;
    }
}

export const addItemAttachment = (viewState: ComposeViewState) => (
    attachmentName: string,
    itemId: string,
    isInline: boolean
): Promise<string> => {
    return addItem(viewState, attachmentName, itemId, isInline).catch(handleError);
};

function handleError(error: any): string {
    throw error.messageId
        ? getAttachmentError(error.messageId)
        : ApiErrorCode.AttachmentUploadGeneralFailure;
}

export const addFileAttachment = (viewState: ComposeViewState) => (
    attachmentName: string,
    uri: string,
    isInline: boolean
): Promise<string> => {
    return addFile(viewState, attachmentName, uri, isInline).catch(handleError);
};

export const getAttachments = (viewState: ComposeViewState) => (): Promise<AttachmentDetails[]> => {
    return lazyGetAllAttachments.importAndExecute(viewState.attachmentWell);
};

export const addFileAttachmentFromBase64 = (viewState: ComposeViewState) => (
    base64File: string,
    attachmentName: string,
    isInline: boolean
): Promise<string> => {
    return createAttachmentFromBase64String(viewState, base64File, attachmentName, isInline).catch(
        handleError
    );
};

function getAttachmentState(
    attachmentStates: AttachmentState[],
    attachmentId: string
): AttachmentState {
    const foundStates = attachmentStates.filter(state => state.attachmentId.Id === attachmentId);
    return foundStates.length > 0 ? foundStates[0] : null;
}

export const removeAttachment = (viewState: ComposeViewState) => (
    attachmentId: string
): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        lazyDeleteAttachmentsViaQueueManager.import().then(deleteAttachmentViaQueueManager => {
            try {
                let attachmentState: AttachmentState = getAttachmentState(
                    viewState.attachmentWell.docViewAttachments,
                    attachmentId
                );
                if (!attachmentState) {
                    attachmentState = getAttachmentState(
                        viewState.attachmentWell.imageViewAttachments,
                        attachmentId
                    );
                }

                if (!attachmentState) {
                    attachmentState = getAttachmentState(
                        viewState.attachmentWell.inlineAttachments,
                        attachmentId
                    );
                }

                if (!attachmentState) {
                    reject(new ApiError(ApiErrorCode.AttachmentToDeleteDoesNotExist));
                    return;
                }

                const parentItemId: ClientItemId = {
                    ...viewState.itemId,
                    mailboxInfo: getCurrentTableMailboxInfo(),
                };
                deleteAttachmentViaQueueManager(
                    viewState.attachmentWell,
                    attachmentState,
                    parentItemId,
                    infoBarAddHandler(viewState),
                    infoBarRemoveHandler(viewState)
                );

                resolve();
            } catch {
                reject(new ApiError(ApiErrorCode.AttachmentDeleteGeneralFailure));
            }
        });
    });
};

export const hasInlineImage = (attachments: AttachmentFileAttributes[]): boolean => {
    if (!attachments || attachments.length == 0) {
        return false;
    }

    const existing = attachments.some((attachment: AttachmentFileAttributes) => {
        return attachment.file.fileType == AttachmentFileType.Uri && attachment.isInline;
    });

    return existing;
};
